import React, { useState, useEffect } from 'react'
import {
  Modal,
  Button,
  Icon,
  Alert,
  Upload,
  Tabs,
  Select,
  Input,
  InputNumber,
  Tooltip,
  Badge
} from 'antd'
import VoiceUploadDetailsModal from '../VoiceUploadDetailsModal'
import CreateVoiceModal from './components/CreateVoiceModal'
import VoiceDetailsModal from './components/VoiceDetailsModal'
import VoiceLibrarySection from './components/VoiceLibrarySection'
import VoiceListItem from './components/VoiceListItem'
import { getVoiceFilters } from '../../../../../services/api/aiVideoGeneration'
import { LANGUAGE_OPTIONS } from '../../../constants/videoLanguages'
import { getVoiceDisplayName, getVoicePreviewUrl } from './utils'
import './VoiceSelectionModal.scss'

const { TabPane } = Tabs
const { Option } = Select
const TABLE_LAYOUT_VARIANTS = new Set(['system', 'custom'])
const FILTER_FIELDS = [
  { key: 'language', label: 'Language' },
  { key: 'gender', label: 'Gender' },
  { key: 'accent', label: 'Accent' },
  { key: 'age_range', label: 'Age Range' },
  { key: 'pace_label', label: 'Pace' },
  { key: 'pitch_range', label: 'Pitch' },
  { key: 'vocal_texture', label: 'Vocal Texture' },
  { key: 'background_noise_level', label: 'Noise Level' },
  { key: 'use_case_tags', label: 'Use Case' },
  { key: 'visibility', label: 'Visibility' },
  { key: 'analysis_status', label: 'Analysis Status' }
]
const DEFAULT_FILTER_FORM = {
  search: '',
  audio_quality_min: undefined,
  sort_by: '',
  sort_order: 'desc'
}
const SORT_OPTIONS = [
  { value: 'audio_quality_score', label: 'Audio Quality' },
  { value: 'voice_name', label: 'Voice Name' },
  { value: 'created_at', label: 'Created Date' }
]
const formatOptionLabel = value => {
  if (typeof value !== 'string') return ''
  const normalized = value.trim()
  if (!normalized) return ''
  return normalized
    .split(/[-_]/)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

const isTableLayoutVariant = variant => TABLE_LAYOUT_VARIANTS.has(variant)

const getTableColumns = variant => {
  if (variant === 'system') return ['Voice', 'Gender', 'Actions']
  if (variant === 'custom') return ['Voice', 'Gender', 'Actions']
  return ['Voice', 'Profile', 'Actions']
}

const VoiceSelectionModal = ({
  visible,
  onCancel,
  onSelect,
  voices,
  loadingVoices,
  handlePlayPreview,
  playingVoice,
  audioProgress,
  selectedVoiceId,
  targetName,
  handleUploadVoice,
  uploading,
  uploadProgress,
  uploadTaskProgress = 0,
  uploadTaskStatus = null,
  uploadTaskMessage = '',
  uploadError,
  setUploadError,
  beforeUpload,
  maxUploadSizeMb = 50,
  fetchVoices,
  pendingUploadFile,
  setPendingUploadFile,
  pendingVoiceName,
  pendingVoiceDescription,
  pendingVoiceLanguage,
  setPendingVoiceName,
  setPendingVoiceDescription,
  setPendingVoiceLanguage,
  onRetryUpload,
  onOverwriteUpload,
  recommendedVoices = [],
  loadingRecommendedVoices = false,
  recommendedVoicesError = null,
  voicesError = null,
  showRecommendedTab = false,
  fetchRecommendedVoices,
  onSynthesizeVoice,
  onSaveSynthesizedVoice,
  synthesizeTaskProgress = 0,
  synthesizeTaskStatus = null,
  synthesizeTaskMessage = '',
  canDeleteVoice,
  onDeleteVoice
}) => {
  const [activeTab, setActiveTab] = useState(
    showRecommendedTab ? 'recommended' : 'system'
  )
  const [detailsView, setDetailsView] = useState(null)
  const [isCreateVoiceModalVisible, setIsCreateVoiceModalVisible] = useState(
    false
  )
  const [isUploadDetailsModalVisible, setUploadDetailsModalVisible] = useState(
    false
  )
  const [isFiltersOpen, setIsFiltersOpen] = useState(false)
  const [voiceFilters, setVoiceFilters] = useState({})
  const [isVoiceFiltersLoading, setIsVoiceFiltersLoading] = useState(false)
  const [voiceFiltersError, setVoiceFiltersError] = useState(null)
  const [appliedFilters, setAppliedFilters] = useState({})
  const [filterForm, setFilterForm] = useState(() => ({
    ...DEFAULT_FILTER_FORM
  }))
  const languageLabelByCode = LANGUAGE_OPTIONS.reduce((acc, option) => {
    acc[String(option.value).toLowerCase()] = option.label
    return acc
  }, {})

  const resolvedTargetName =
    typeof targetName === 'object'
      ? targetName?.name
      : typeof targetName === 'string'
      ? targetName.trim()
      : ''
  const modalTargetName =
    resolvedTargetName || (showRecommendedTab ? 'Narrator' : '')

  const systemVoices = voices.filter(v => v.type !== 'uploaded')
  const customVoices = voices.filter(v => v.type === 'uploaded')

  const hasSystemVoices = systemVoices.length > 0
  const hasCustomVoices = customVoices.length > 0
  const hasRecommendedVoices = recommendedVoices.length > 0
  const activeFilterCount = Object.entries(appliedFilters).filter(
    ([key, value]) => !['limit', 'offset', 'sort_order'].includes(key) && value
  ).length

  const fetchVoiceFilterOptions = async () => {
    if (isVoiceFiltersLoading || Object.keys(voiceFilters || {}).length) return

    setIsVoiceFiltersLoading(true)
    setVoiceFiltersError(null)

    try {
      const response = await getVoiceFilters()
      const filters = response?.data?.filters || {}
      setVoiceFilters(filters)
    } catch (error) {
      setVoiceFilters({})
      setVoiceFiltersError('Unable to load voice filters right now.')
    } finally {
      setIsVoiceFiltersLoading(false)
    }
  }

  const updateFilterField = (key, value) => {
    setFilterForm(prev => ({
      ...prev,
      [key]: value
    }))
  }

  const buildVoiceFilterParams = () => {
    const params = {}

    Object.entries(filterForm).forEach(([key, value]) => {
      if (key === 'sort_order') return

      if (Array.isArray(value) && value.length) {
        params[key] = value.join(',')
      } else if (typeof value === 'string' && value.trim()) {
        params[key] = value.trim()
      } else if (typeof value === 'number') {
        params[key] = value
      }
    })

    FILTER_FIELDS.forEach(({ key }) => {
      const value = filterForm[key]
      if (Array.isArray(value) && value.length) {
        params[key] = value.join(',')
      }
    })

    if (params.sort_by) {
      params.sort_order = filterForm.sort_order || 'desc'
    }

    params.limit = 20
    params.offset = 0

    return params
  }

  const handleApplyFilters = async () => {
    if (typeof fetchVoices !== 'function') return
    const params = buildVoiceFilterParams()
    setAppliedFilters(params)
    await fetchVoices(params)
  }

  const handleResetFilters = async () => {
    if (typeof fetchVoices !== 'function') return
    setFilterForm({ ...DEFAULT_FILTER_FORM })
    setAppliedFilters({})
    await fetchVoices({})
  }

  useEffect(() => {
    if (visible) {
      fetchVoiceFilterOptions()
    }

    if (!visible) {
      setIsCreateVoiceModalVisible(false)
      setIsFiltersOpen(false)
    }
  }, [visible])

  useEffect(() => {
    if (!visible) return

    setActiveTab(currentTab => {
      if (showRecommendedTab) return 'recommended'
      return currentTab === 'recommended' ? 'system' : currentTab
    })
  }, [visible, showRecommendedTab])

  const openUploadDetails = file => {
    if (setPendingUploadFile) {
      setPendingUploadFile(file)
    }
    setPendingVoiceName('')
    setPendingVoiceDescription('')
    setPendingVoiceLanguage('')
    setUploadDetailsModalVisible(true)
  }

  const closeUploadDetails = () => {
    setUploadDetailsModalVisible(false)
    if (setPendingUploadFile) {
      setPendingUploadFile(null)
    }
    setPendingVoiceName('')
    setPendingVoiceDescription('')
    setPendingVoiceLanguage('')
  }

  const handleFileSelect = file => {
    const fileAllowed = beforeUpload ? beforeUpload(file) : true
    if (fileAllowed !== false) {
      openUploadDetails(file)
    }
    return false
  }

  const handleConfirmUpload = async () => {
    if (!pendingUploadFile) return
    if (!pendingVoiceName?.trim()) {
      setUploadError('Voice name is required.')
      return
    }
    await handleUploadVoice({
      file: pendingUploadFile,
      onSuccess: () => closeUploadDetails(),
      onError: () => {}
    })
  }

  const handleRetryUploadWithClose = async () => {
    if (typeof onRetryUpload !== 'function') return
    await onRetryUpload({ onSuccess: () => closeUploadDetails() })
  }

  const handleOverwriteUploadWithClose = async () => {
    if (typeof onOverwriteUpload !== 'function') return
    await onOverwriteUpload({ onSuccess: () => closeUploadDetails() })
  }

  const normalizeLanguage = language => {
    if (typeof language !== 'string') return ''
    return language.trim()
  }

  const getVoiceLanguageLabel = voice => {
    const directLanguage = normalizeLanguage(voice?.language)
    if (directLanguage) {
      const normalized = directLanguage.toLowerCase()
      return languageLabelByCode[normalized] || directLanguage
    }

    const firstLanguageFromList = Array.isArray(voice?.languages)
      ? normalizeLanguage(voice.languages[0])
      : ''
    if (firstLanguageFromList) {
      const normalized = firstLanguageFromList.toLowerCase()
      return languageLabelByCode[normalized] || firstLanguageFromList
    }

    return 'Other Languages'
  }

  const groupVoicesByLanguage = voiceList => {
    const groupsMap = new Map()

    voiceList.forEach(voice => {
      const label = getVoiceLanguageLabel(voice)
      const key = label.toLowerCase()

      if (!groupsMap.has(key)) {
        groupsMap.set(key, {
          key,
          label,
          voices: []
        })
      }

      groupsMap.get(key).voices.push(voice)
    })

    return Array.from(groupsMap.values()).sort((a, b) => {
      if (a.label === 'Other Languages') return 1
      if (b.label === 'Other Languages') return -1
      return a.label.localeCompare(b.label)
    })
  }

  const renderGroupedVoiceList = (voiceList, variant) => {
    const groupedVoices = groupVoicesByLanguage(voiceList)
    const isTableLayout = isTableLayoutVariant(variant)
    const [firstColumn, secondColumn, thirdColumn] = getTableColumns(variant)

    return (
      <>
        {voiceList.length > 0 ? (
          <div
            className={`voice-selection-modal__voice-list voice-selection-modal__voice-list--${variant} ${
              isTableLayout ? 'voice-selection-modal__voice-list--table' : ''
            } ${
              variant === 'custom' ? 'voice-selection-modal__voice-grid' : ''
            }`}
          >
            {isTableLayout && (
              <div
                className={`voice-selection-modal__table-columns voice-selection-modal__table-columns--${variant}`}
              >
                <span>{firstColumn}</span>
                <span>{secondColumn}</span>
                <span>{thirdColumn}</span>
              </div>
            )}

            {groupedVoices.map(group => (
              <section
                key={group.key}
                className={`voice-selection-modal__language-group voice-selection-modal__language-group--${variant} ${
                  isTableLayout
                    ? 'voice-selection-modal__language-group--table'
                    : ''
                }`}
              >
                <div className="voice-selection-modal__language-group-header">
                  <h4>{group.label} Voices</h4>
                  <div className="voice-selection-modal__language-group-meta">
                    <span>
                      {group.voices.length} voice
                      {group.voices.length === 1 ? '' : 's'}
                    </span>
                  </div>
                </div>

                {!isTableLayout && (
                  <div className="voice-selection-modal__language-group-columns">
                    <span>Voice</span>
                    <span>Profile</span>
                    <span>Actions</span>
                  </div>
                )}

                <div
                  className={`voice-selection-modal__language-group-list voice-selection-modal__language-group-list--${variant}`}
                >
                  {group.voices.map((voice, index) => {
                    const displayName = getVoiceDisplayName(
                      voice,
                      variant,
                      index
                    )

                    return (
                      <VoiceListItem
                        key={voice.id}
                        voice={voice}
                        variant={variant}
                        displayName={displayName}
                        isSelected={selectedVoiceId === voice.id}
                        isPlaying={playingVoice === voice.id}
                        progress={playingVoice === voice.id ? audioProgress : 0}
                        onSelect={() => onSelect(voice.id)}
                        onViewDetails={() =>
                          setDetailsView({
                            voice,
                            variant,
                            displayName
                          })
                        }
                        onPlay={() =>
                          handlePlayPreview(voice.id, getVoicePreviewUrl(voice))
                        }
                        canDelete={
                          typeof canDeleteVoice === 'function'
                            ? canDeleteVoice(voice)
                            : false
                        }
                        onDelete={() =>
                          typeof onDeleteVoice === 'function'
                            ? onDeleteVoice(voice)
                            : null
                        }
                      />
                    )
                  })}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <div className="voice-selection-modal__state-card voice-selection-modal__state-card--empty">
            <Icon type="sound" />
            <p>No voices available for this list.</p>
          </div>
        )}
      </>
    )
  }

  const renderVoiceFilters = () => (
    <div
      className={`voice-selection-modal__filters ${
        isFiltersOpen ? 'voice-selection-modal__filters--open' : ''
      }`}
    >
      {isFiltersOpen && (
        <>
          {voiceFiltersError && (
            <Alert
              message={voiceFiltersError}
              type="warning"
              showIcon
              className="voice-selection-modal__alert"
            />
          )}
          <div className="voice-selection-modal__filter-grid">
            <div className="voice-selection-modal__filter-field voice-selection-modal__filter-field--wide">
              <span>Search</span>
              <Input
                allowClear
                value={filterForm.search}
                onChange={event =>
                  updateFilterField('search', event.target.value)
                }
                placeholder="Search voice name or style"
                prefix={<Icon type="search" />}
              />
            </div>

            {FILTER_FIELDS.map(({ key, label }) => (
              <div key={key} className="voice-selection-modal__filter-field">
                <span>{label}</span>
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  value={filterForm[key] || []}
                  onChange={value => updateFilterField(key, value)}
                  placeholder={`Any ${label.toLowerCase()}`}
                  loading={isVoiceFiltersLoading}
                  disabled={isVoiceFiltersLoading}
                >
                  {(voiceFilters[key] || []).map(option => (
                    <Option key={option} value={option}>
                      {formatOptionLabel(option)}
                    </Option>
                  ))}
                </Select>
              </div>
            ))}

            <div className="voice-selection-modal__filter-field">
              <span>Min Quality</span>
              <InputNumber
                min={0}
                max={10}
                step={0.5}
                value={filterForm.audio_quality_min}
                onChange={value =>
                  updateFilterField('audio_quality_min', value)
                }
                placeholder="0-10"
              />
            </div>

            <div className="voice-selection-modal__filter-field">
              <span>Sort By</span>
              <Select
                allowClear
                value={filterForm.sort_by || undefined}
                onChange={value => updateFilterField('sort_by', value || '')}
                placeholder="Default"
              >
                {SORT_OPTIONS.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </div>

            <div className="voice-selection-modal__filter-field">
              <span>Sort Order</span>
              <Select
                value={filterForm.sort_order}
                onChange={value => updateFilterField('sort_order', value)}
              >
                <Option value="desc">Descending</Option>
                <Option value="asc">Ascending</Option>
              </Select>
            </div>
          </div>

          <div className="voice-selection-modal__filter-actions">
            <Button onClick={handleResetFilters}>Reset</Button>
            <Button
              type="primary"
              icon="check"
              onClick={handleApplyFilters}
              loading={loadingVoices}
            >
              Apply Filters
            </Button>
          </div>
        </>
      )}
    </div>
  )

  const renderTabActions = () => (
    <div className="voice-selection-modal__tab-actions">
      <Tooltip title={isFiltersOpen ? 'Hide filters' : 'Show filters'}>
        <Button
          className={`voice-selection-modal__filter-toggle ${
            isFiltersOpen
              ? 'voice-selection-modal__filter-toggle--active'
              : ''
          }`}
          onClick={() => {
            setActiveTab('system')
            setIsFiltersOpen(prev => !prev)
            fetchVoiceFilterOptions()
          }}
        >
          <span className="voice-selection-modal__filter-label-text">
            Filters
          </span>
          <Badge
            count={activeFilterCount}
            className="voice-selection-modal__filter-badge"
          />
          <Icon type={isFiltersOpen ? 'up' : 'down'} />
        </Button>
      </Tooltip>
      <Tooltip title="Create voice">
        <Button
          className="voice-selection-modal__create-btn"
          onClick={() => setIsCreateVoiceModalVisible(true)}
        >
          <Icon type="plus" />
          <span>Create Voice</span>
        </Button>
      </Tooltip>
    </div>
  )

  return (
    <Modal
      maskClosable={false}
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width={880}
      title={
        modalTargetName ? `Select Voice for ${modalTargetName}` : 'Select Voice'
      }
      centered
      className="voice-selection-modal"
      destroyOnClose
    >
      <div className="voice-selection-modal__content">
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          animated={false}
          tabBarExtraContent={renderTabActions()}
        >
          {showRecommendedTab && (
            <TabPane
              tab={
                <span>
                  <Icon type="star" />
                  Recommended
                </span>
              }
              key="recommended"
            >
              <div className="voice-selection-modal__tab-content voice-selection-modal__tab-content--recommended">
                <div className="voice-selection-modal__recommendation-hero">
                  <div className="voice-selection-modal__recommendation-copy">
                    <h3>Recommended for your story</h3>
                    <p>
                      Start with the top match, compare a few options, and use
                      the eye icon to inspect each voice before choosing.
                    </p>
                  </div>
                  {typeof fetchRecommendedVoices === 'function' && (
                    <Button
                      type="default"
                      icon={hasRecommendedVoices ? 'reload' : 'bulb'}
                      onClick={fetchRecommendedVoices}
                      loading={loadingRecommendedVoices}
                      className="voice-selection-modal__refresh-btn"
                    >
                      {hasRecommendedVoices
                        ? 'Refresh'
                        : 'Generate Recommendations'}
                    </Button>
                  )}
                </div>

                {recommendedVoicesError && (
                  <Alert
                    message={recommendedVoicesError}
                    type="warning"
                    showIcon
                    className="voice-selection-modal__alert"
                  />
                )}

                {loadingRecommendedVoices ? (
                  <div className="voice-selection-modal__state-card">
                    <div className="processing-visual">
                      <div className="custom-loader" />
                      <div className="loader-center">
                        <div className="inner-dot" />
                      </div>
                    </div>
                    <p>Curating recommended voices...</p>
                  </div>
                ) : recommendedVoices.length > 0 ? (
                  renderGroupedVoiceList(recommendedVoices, 'recommended')
                ) : (
                  <div className="voice-selection-modal__state-card voice-selection-modal__state-card--empty">
                    <Icon type="bulb" />
                    <p>No recommendations available for this story yet.</p>
                  </div>
                )}
              </div>
            </TabPane>
          )}

          <TabPane
            tab={
              <span>
                <Icon type="customer-service" />
                System
              </span>
            }
            key="system"
          >
            <div className="voice-selection-modal__tab-content voice-selection-modal__tab-content--system">
              {renderVoiceFilters()}
              <VoiceLibrarySection
                title="System voices"
                description="Choose from our library of high-quality AI voices with a cleaner, easier comparison layout."
                onRefresh={fetchVoices}
                isRefreshing={loadingVoices}
                error={voicesError}
                isLoading={loadingVoices}
                loadingText="Loading available system voices..."
                emptyIcon="customer-service"
                emptyText="No system voices are available right now."
              >
                {hasSystemVoices &&
                  renderGroupedVoiceList(systemVoices, 'system')}
              </VoiceLibrarySection>
            </div>
          </TabPane>

          <TabPane
            tab={
              <span>
                <Icon type="user" />
                Custom
              </span>
            }
            key="custom"
          >
            <div className="voice-selection-modal__tab-content voice-selection-modal__tab-content--custom">
              <VoiceLibrarySection
                title="Custom voices"
                description="Upload your own sample, then compare saved custom voices using the same layout as the library."
                onRefresh={fetchVoices}
                isRefreshing={loadingVoices}
                error={voicesError}
                isLoading={false}
                emptyIcon="user"
                emptyText="No custom voices uploaded yet."
              >
                <div className="voice-selection-modal__upload-section">
                  {uploadError && !isUploadDetailsModalVisible && (
                    <Alert
                      message={uploadError}
                      type="error"
                      showIcon
                      closable
                      onClose={() => setUploadError(null)}
                      className="voice-selection-modal__alert"
                    />
                  )}

                  <Upload.Dragger
                    accept=".mp3"
                    beforeUpload={handleFileSelect}
                    showUploadList={false}
                  >
                    <p className="ant-upload-drag-icon">
                      <Icon type="cloud-upload" />
                    </p>
                    <p className="ant-upload-text">
                      Click or drag audio file to start uploading a custom
                      voice.
                    </p>
                    <p className="ant-upload-hint">
                      Upload an MP3 file first, then add name, description, and
                      an optional language.
                    </p>
                    <p className="voice-selection-modal__upload-conditions">
                      Allowed format: MP3. File size: up to {maxUploadSizeMb}
                      MB.
                    </p>
                  </Upload.Dragger>

                  <VoiceUploadDetailsModal
                    visible={isUploadDetailsModalVisible}
                    pendingUploadFile={pendingUploadFile}
                    uploadError={uploadError}
                    uploading={uploading}
                    uploadProgress={uploadProgress}
                    uploadTaskProgress={uploadTaskProgress}
                    uploadTaskStatus={uploadTaskStatus}
                    uploadTaskMessage={uploadTaskMessage}
                    pendingVoiceName={pendingVoiceName}
                    pendingVoiceDescription={pendingVoiceDescription}
                    pendingVoiceLanguage={pendingVoiceLanguage}
                    setPendingVoiceName={setPendingVoiceName}
                    setPendingVoiceDescription={setPendingVoiceDescription}
                    setPendingVoiceLanguage={setPendingVoiceLanguage}
                    onCancel={closeUploadDetails}
                    onConfirmUpload={handleConfirmUpload}
                    onRetryUpload={handleRetryUploadWithClose}
                    onOverwriteUpload={handleOverwriteUploadWithClose}
                  />
                </div>

                {loadingVoices ? (
                  <div className="voice-selection-modal__state-card">
                    <div className="processing-visual">
                      <div className="custom-loader" />
                      <div className="loader-center">
                        <div className="inner-dot" />
                      </div>
                    </div>
                    <p>Loading your saved custom voices...</p>
                  </div>
                ) : hasCustomVoices ? (
                  renderGroupedVoiceList(customVoices, 'custom')
                ) : (
                  <div className="voice-selection-modal__state-card voice-selection-modal__state-card--empty">
                    <Icon type="user" />
                    <p>No custom voices uploaded yet.</p>
                  </div>
                )}
              </VoiceLibrarySection>
            </div>
          </TabPane>
        </Tabs>
      </div>

      <VoiceDetailsModal
        visible={!!detailsView}
        voice={detailsView?.voice}
        variant={detailsView?.variant}
        displayName={detailsView?.displayName}
        onClose={() => setDetailsView(null)}
      />

      <CreateVoiceModal
        visible={isCreateVoiceModalVisible}
        onCancel={() => setIsCreateVoiceModalVisible(false)}
        onGenerate={onSynthesizeVoice}
        onSave={onSaveSynthesizedVoice}
        taskProgress={synthesizeTaskProgress}
        taskStatus={synthesizeTaskStatus}
        taskMessage={synthesizeTaskMessage}
      />
    </Modal>
  )
}

export default VoiceSelectionModal
